import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "~/lib/utils"

const sliderVariants = cva("h-slider", {
  variants: {
    size: {
      sm: "h-slider--sm",
      md: "h-slider--md",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

export type SliderProps = React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> &
  VariantProps<typeof sliderVariants> & {
    /**
     * Visual snap-point dots (like the AI assistant length slider).
     * Purely presentational; does not affect the slider behavior.
     */
    showMarks?: boolean
  }

const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  ({ className, size, value, defaultValue, min = 0, max = 100, step = 1, showMarks = false, ...props }, ref) => {
    const values = React.useMemo(() => {
      if (Array.isArray(value)) return value
      if (Array.isArray(defaultValue)) return defaultValue
      return [min]
    }, [value, defaultValue, min])

    const marks = React.useMemo(() => {
      if (!showMarks) return null
      const marksList: number[] = []
      for (let v = min; v <= max; v += step) {
        marksList.push(v)
      }
      return marksList
    }, [min, max, step, showMarks])

    return (
      <SliderPrimitive.Root
        ref={ref}
        className={cn(sliderVariants({ size }), className)}
        value={value}
        defaultValue={defaultValue}
        min={min}
        max={max}
        step={step}
        {...props}
      >
        <SliderPrimitive.Track className="h-slider__track">
          <SliderPrimitive.Range className="h-slider__range" />
          {marks && (
            <div className="h-slider__marks" aria-hidden="true">
              {marks.map((mark) => (
                <span
                  key={mark}
                  className="h-slider__mark"
                  style={{
                    left: max === min ? "0%" : `${((mark - min) / (max - min)) * 100}%`,
                  }}
                />
              ))}
            </div>
          )}
        </SliderPrimitive.Track>

        {values.map((_, index) => (
          <SliderPrimitive.Thumb key={index} className="h-slider__thumb" />
        ))}
      </SliderPrimitive.Root>
    )
  },
)

Slider.displayName = "Slider"

export { Slider }
